import React, { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Wallet, Plus, Minus, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import CurrencyInput from '../../components/common/CurrencyInput';

// Alert asking if user wants to generate payments
export const PaymentsAlertDialog = ({
  open,
  onOpenChange,
  onGenerateNow,
  onGenerateLater,
  activityCost,
  formatCurrency,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-green-500" />
            Gerar pagamentos?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Deseja gerar pagamentos entre os membros desta atividade?
            <span className="block text-xs text-slate-500 mt-2">
              Lembrando que você poderá fazer isso mais tarde.
            </span>
            {activityCost > 0 && (
              <span className="block text-sm text-green-400 mt-2">
                Valor: {formatCurrency(activityCost)}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onGenerateLater} className="w-full sm:w-auto">
            Fazer mais tarde
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onGenerateNow}
            className="w-full sm:w-auto bg-green-500 hover:bg-green-600"
          >
            <Wallet className="w-4 h-4 mr-2" />
            Gerar agora
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Alert for existing payments
export const ExistingPaymentsAlert = ({
  open,
  onOpenChange,
  existingPayments,
  formatCurrency,
  onCancel,
  onDelete,
  onRecreate,
}) => {
  const totalAmount = existingPayments?.reduce((sum, p) => sum + (p.total_amount || 0), 0) || 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-yellow-500" />
            Pagamentos existentes
          </AlertDialogTitle>
          <AlertDialogDescription>
            Já existem pagamentos gerados para os membros sobre este roteiro.
            <span className="block text-sm text-slate-400 mt-2">
              {existingPayments?.length || 0} pagamento(s) encontrado(s) no valor total de{' '}
              <span className="text-white font-semibold">
                {formatCurrency(totalAmount)}
              </span>
            </span>
            <span className="block text-xs text-slate-500 mt-2">
              O que deseja fazer?
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onCancel} className="w-full sm:w-auto">
            Cancelar
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onDelete}
            className="w-full sm:w-auto"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Excluir pagamentos
          </Button>
          <AlertDialogAction 
            onClick={onRecreate}
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refazer rateio
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Alert for marking activity as free (deletes existing payments)
export const MarkAsFreeAlert = ({
  open,
  onOpenChange,
  existingPayments,
  formatCurrency,
  onCancel,
  onConfirm,
}) => {
  const totalAmount = existingPayments?.reduce((sum, p) => sum + (p.total_amount || 0), 0) || 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Marcar como gratuito?
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block text-yellow-200 bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mt-2">
              <strong>Atenção:</strong> Esta atividade possui{' '}
              <span className="font-semibold">{existingPayments?.length || 0} pagamento(s)</span> no valor total de{' '}
              <span className="text-white font-semibold">{formatCurrency(totalAmount)}</span>.
            </span>
            <span className="block text-sm text-slate-400 mt-3">
              Ao marcar como gratuito, todos os pagamentos vinculados a esta atividade serão <span className="text-red-400 font-semibold">excluídos permanentemente</span>.
            </span>
            <span className="block text-xs text-slate-500 mt-2">
              Deseja continuar?
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onCancel} className="w-full sm:w-auto">
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className="w-full sm:w-auto bg-red-500 hover:bg-red-600"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Excluir e marcar gratuito
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Dialog for payment split distribution
export const PaymentSplitDialog = ({
  open,
  onOpenChange,
  activityTitle,
  activityCost,
  paymentSplitData,
  setPaymentSplitData,
  formatCurrency,
  onSave,
  onCancel,
}) => {
  const totalParticipants = paymentSplitData?.length || 0;
  const quotaValue = totalParticipants > 0 ? activityCost / totalParticipants : 0;
  const totalQuotas = paymentSplitData?.reduce((sum, p) => sum + p.quotas, 0) || 0;

  const handleDecreaseQuota = (index) => {
    if (!paymentSplitData) return;
    const newData = [...paymentSplitData];
    if (newData[index].quotas > 0) {
      newData[index].quotas -= 1;
      setPaymentSplitData(newData);
    }
  };

  const handleIncreaseQuota = (index) => {
    if (!paymentSplitData) return;
    const newData = [...paymentSplitData];
    newData[index].quotas += 1;
    setPaymentSplitData(newData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-green-500" />
            Organizar pagamento
          </DialogTitle>
          <DialogDescription>
            {activityTitle && (
              <span className="block mb-2">
                Atividade: <span className="text-white font-medium">&quot;{activityTitle}&quot;</span>
              </span>
            )}
            Valor: <span className="text-white font-semibold">{formatCurrency(activityCost)}</span>
            {' | '}
            {totalParticipants} participantes = {totalParticipants} cotas de{' '}
            <span className="text-green-400 font-semibold">{formatCurrency(quotaValue)}</span>
            <br />
            <span className={cn(
              "text-xs",
              totalQuotas === totalParticipants ? "text-green-400" : "text-yellow-400"
            )}>
              Cotas distribuídas: {totalQuotas}/{totalParticipants}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {paymentSplitData?.map((participant, index) => {
            const participantTotal = participant.quotas * quotaValue;

            return (
              <div
                key={participant.user_id}
                className="flex items-center justify-between bg-slate-800 rounded-lg p-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{participant.user_name}</p>
                  {participant.quotas > 0 && (
                    <p className="text-xs text-green-400">
                      {participant.quotas} {participant.quotas === 1 ? 'cota' : 'cotas'} = {formatCurrency(participantTotal)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDecreaseQuota(index)}
                    disabled={participant.quotas === 0}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-8 text-center text-lg font-bold text-white">
                    {participant.quotas}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleIncreaseQuota(index)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={onSave}
            className="w-full sm:w-auto bg-green-500 hover:bg-green-600"
            disabled={totalQuotas === 0}
          >
            <Wallet className="w-4 h-4 mr-2" />
            Gerar Pagamentos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog for activity payments (with editable amount)
export const ActivityPaymentsDialog = ({
  open,
  onOpenChange,
  activity,
  paymentAmount,
  setPaymentAmount,
  isFree,
  setIsFree,
  paymentSplitData,
  setPaymentSplitData,
  tripCurrency,
  formatCurrency,
  onSave,
  onCancel,
  onMarkAsFree,
  hasExistingPayments,
  hasExistingQuotas,
  isRefreshingPayment,
  setIsRefreshingPayment,
}) => {
  const contentRef = useRef(null);
  const [isManualMode, setIsManualMode] = React.useState(false);
  
  const totalParticipants = paymentSplitData?.length || 0;
  const quotaValue = totalParticipants > 0 ? paymentAmount / totalParticipants : 0;
  const totalQuotas = paymentSplitData?.reduce((sum, p) => sum + p.quotas, 0) || 0;
  
  // Calculate total assigned in manual mode
  const totalManualAssigned = paymentSplitData?.reduce((sum, p) => sum + (p.manualAmount || 0), 0) || 0;
  const remainingAmount = paymentAmount - totalManualAssigned;

  // Check if all quotas are distributed (quota mode) or all amount assigned (manual mode)
  const isComplete = isManualMode 
    ? Math.abs(remainingAmount) < 0.01 
    : totalQuotas === totalParticipants;
  
  // Check if any member has started paying
  const hasStartedPayments = paymentSplitData?.some(p => p.amount_paid > 0 || p.is_paid);

  // Determine if editing is allowed
  const canEdit = !hasExistingQuotas || isRefreshingPayment;

  // Detect manual mode when dialog opens with existing data
  React.useEffect(() => {
    if (open && paymentSplitData) {
      // Check if any participant has manualAmount > 0, indicating manual distribution
      const hasManualAmounts = paymentSplitData.some(p => (p.manualAmount || 0) > 0);
      setIsManualMode(hasManualAmounts);
    }
  }, [open, paymentSplitData]);

  const handleDecreaseQuota = (index) => {
    if (!paymentSplitData || !canEdit) return;
    // If in manual mode, switch back to quota mode
    if (isManualMode) {
      setIsManualMode(false);
      const resetData = paymentSplitData.map(p => ({ ...p, quotas: 0, manualAmount: 0 }));
      setPaymentSplitData(resetData);
      return;
    }
    const newData = [...paymentSplitData];
    if (newData[index].quotas > 0) {
      newData[index].quotas -= 1;
      setPaymentSplitData(newData);
    }
  };

  const handleIncreaseQuota = (index) => {
    if (!paymentSplitData || !canEdit) return;
    // If in manual mode, switch back to quota mode
    if (isManualMode) {
      setIsManualMode(false);
      const resetData = paymentSplitData.map(p => ({ ...p, quotas: 0, manualAmount: 0 }));
      setPaymentSplitData(resetData);
      return;
    }
    const newData = [...paymentSplitData];
    newData[index].quotas += 1;
    setPaymentSplitData(newData);
  };

  const handleManualAmountChange = (index, value) => {
    if (!paymentSplitData || !canEdit) return;
    // Switch to manual mode
    if (!isManualMode) {
      setIsManualMode(true);
    }
    const newData = [...paymentSplitData];
    newData[index].manualAmount = value;
    // Reset quotas when using manual mode
    newData[index].quotas = 0;
    setPaymentSplitData(newData);
  };

  const handleFreeToggle = (checked) => {
    if (!canEdit) return;
    if (checked && hasExistingPayments) {
      onMarkAsFree?.();
    } else {
      setIsFree(checked);
      if (checked) {
        setPaymentAmount(0);
      }
    }
  };

  const handleRefreshToggle = (checked) => {
    setIsRefreshingPayment(checked);
    setIsManualMode(false);
    if (checked) {
      // Reset quotas and manual amounts to 0 when enabling refresh
      const resetData = paymentSplitData?.map(p => ({ ...p, quotas: 0, manualAmount: 0 })) || [];
      setPaymentSplitData(resetData);
    }
  };

  // Scroll to input when focused (mobile keyboard fix)
  const handleInputFocus = async () => {
    await new Promise(resolve => setTimeout(resolve, 300));
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Calculate display values based on mode
  const getParticipantAmount = (participant) => {
    if (isManualMode) {
      return participant.manualAmount || 0;
    }
    return participant.quotas * quotaValue;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" ref={contentRef}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-green-500" />
            {hasExistingQuotas ? 'Rateio do Pagamento' : 'Gerar Pagamento'}
          </DialogTitle>
          <DialogDescription>
            {activity?.title && (
              <span className="block mb-2">
                Atividade: <span className="text-white font-medium">&quot;{activity.title}&quot;</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Existing quotas banner */}
          {hasExistingQuotas && !isRefreshingPayment && (
            <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3">
              <p className="text-sm text-blue-200">
                <strong>Rateio já definido</strong>
              </p>
              <p className="text-xs text-blue-300 mt-1">
                Este roteiro já possui um rateio configurado. Para modificar, ative a opção &quot;Refazer rateio&quot; abaixo.
              </p>
            </div>
          )}

          {/* Refresh toggle - only show if there are existing quotas */}
          {hasExistingQuotas && !hasStartedPayments && (
            <div className="flex items-center justify-between bg-orange-500/20 border border-orange-500/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-orange-200">Refazer rateio</span>
              </div>
              <Switch
                checked={isRefreshingPayment}
                onCheckedChange={handleRefreshToggle}
                data-testid="refresh-toggle"
              />
            </div>
          )}

          {/* Warning if payments already started */}
          {hasStartedPayments && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
              <p className="text-sm text-red-200">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                <strong>Pagamentos já iniciados</strong>
              </p>
              <p className="text-xs text-red-300 mt-1">
                Alguns membros já realizaram pagamentos. Não é possível modificar o rateio.
              </p>
            </div>
          )}

          {/* Free Toggle - Above the input */}
          {!hasStartedPayments && (
            <div className={cn(
              "flex items-center justify-between rounded-lg p-3",
              canEdit ? "bg-slate-800" : "bg-slate-800/50 opacity-60"
            )}>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-300">Atividade gratuita</span>
              </div>
              <Switch
                checked={isFree}
                onCheckedChange={handleFreeToggle}
                disabled={!canEdit}
                data-testid="free-toggle"
              />
            </div>
          )}

          {/* Payment Amount Input - Only show if not free */}
          {!isFree && (
            <>
              <div className="space-y-2">
                <Label>Valor total do pagamento</Label>
                <CurrencyInput
                  value={paymentAmount}
                  onChange={setPaymentAmount}
                  onFocus={handleInputFocus}
                  currency={tripCurrency}
                  placeholder="0,00"
                  disabled={!canEdit}
                  data-testid="payment-amount"
                />
                <p className="text-xs text-slate-500">
                  {paymentAmount > 0 && totalParticipants > 0 && !isManualMode && (
                    <>
                      {totalParticipants} participantes = {totalParticipants} cotas de{' '}
                      <span className="text-green-400 font-semibold">
                        {formatCurrency(quotaValue)}
                      </span>
                    </>
                  )}
                </p>
              </div>

              {/* Remaining amount indicator for manual mode */}
              {paymentAmount > 0 && isManualMode && (
                <div className={cn(
                  "p-3 rounded-lg border text-center",
                  Math.abs(remainingAmount) < 0.01 
                    ? "bg-green-500/20 border-green-500/50" 
                    : remainingAmount > 0 
                      ? "bg-yellow-500/20 border-yellow-500/50"
                      : "bg-red-500/20 border-red-500/50"
                )}>
                  <p className="text-xs text-slate-400">Valor restante a distribuir:</p>
                  <p className={cn(
                    "text-lg font-bold",
                    Math.abs(remainingAmount) < 0.01 
                      ? "text-green-400" 
                      : remainingAmount > 0 
                        ? "text-yellow-400"
                        : "text-red-400"
                  )}>
                    {formatCurrency(remainingAmount)}
                  </p>
                  {Math.abs(remainingAmount) < 0.01 && (
                    <p className="text-xs text-green-400 mt-1">✓ Valor totalmente distribuído</p>
                  )}
                </div>
              )}

              {/* Distribution Section */}
              {paymentAmount > 0 && totalParticipants > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">
                      {isManualMode ? 'Valores manuais' : 'Distribuir cotas'}
                    </Label>
                    {!isManualMode && (
                      <span className={cn(
                        "text-xs",
                        isComplete ? "text-green-400" : "text-yellow-400"
                      )}>
                        {totalQuotas}/{totalParticipants} cotas
                      </span>
                    )}
                    {isManualMode && (
                      <span className="text-xs text-orange-400">
                        Modo manual
                      </span>
                    )}
                  </div>

                  {/* Hint text */}
                  <p className="text-xs text-slate-500 -mt-1">
                    {isManualMode 
                      ? 'Digite o valor de cada pessoa. Use +/- para voltar ao modo cotas.'
                      : 'Use +/- para cotas iguais ou digite o valor para modo manual.'}
                  </p>

                  <div className="space-y-2 max-h-[30vh] overflow-y-auto">
                    {paymentSplitData?.map((participant, index) => {
                      const participantAmount = getParticipantAmount(participant);

                      return (
                        <div
                          key={participant.user_id}
                          className={cn(
                            "flex items-center justify-between rounded-lg p-3",
                            participant.is_paid ? "bg-green-900/30 border border-green-500/30" : "bg-slate-800"
                          )}
                        >
                          <div className="flex-1 min-w-0 mr-2">
                            <p className="text-sm font-medium text-white truncate">
                              {participant.user_name}
                              {participant.is_paid && (
                                <span className="ml-2 text-xs text-green-400">(Pago)</span>
                              )}
                            </p>
                            {participantAmount > 0 && !isManualMode && (
                              <p className="text-xs text-green-400">
                                {participant.quotas} {participant.quotas === 1 ? 'cota' : 'cotas'} = {formatCurrency(participantAmount)}
                              </p>
                            )}
                            {participant.amount_paid > 0 && (
                              <p className="text-xs text-slate-400">
                                (Pago: {formatCurrency(participant.amount_paid)})
                              </p>
                            )}
                          </div>
                          
                          {/* Controls: Manual input + Quota buttons */}
                          <div className="flex items-center gap-2">
                            {/* Manual Value Input */}
                            <div className="w-24">
                              <CurrencyInput
                                value={isManualMode ? (participant.manualAmount || 0) : participantAmount}
                                onChange={(val) => handleManualAmountChange(index, val)}
                                onFocus={handleInputFocus}
                                currency={tripCurrency}
                                placeholder="0,00"
                                disabled={!canEdit || participant.is_paid}
                                className="h-8 text-sm"
                                data-testid={`manual-amount-${index}`}
                              />
                            </div>
                            
                            {/* Quota buttons */}
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={() => handleDecreaseQuota(index)}
                              disabled={(!isManualMode && participant.quotas === 0) || !canEdit || participant.is_paid}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            {!isManualMode && (
                              <span className="w-6 text-center text-sm font-bold text-white">
                                {participant.quotas}
                              </span>
                            )}
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={() => handleIncreaseQuota(index)}
                              disabled={!canEdit || participant.is_paid}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {/* Message when marked as free */}
          {isFree && (
            <div className="bg-slate-800/50 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-400">
                Esta atividade está marcada como <span className="text-green-400 font-semibold">gratuita</span>.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Nenhum pagamento será gerado.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            {hasExistingQuotas && !isRefreshingPayment ? 'Fechar' : 'Cancelar'}
          </Button>
          {/* Only show save button if we can make changes */}
          {(canEdit || isFree) && !hasStartedPayments && (
            <Button
              onClick={onSave}
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600"
              disabled={!isFree && (paymentAmount <= 0 || !isComplete)}
            >
              <Wallet className="w-4 h-4 mr-2" />
              {isFree 
                ? 'Salvar como gratuito' 
                : hasExistingQuotas 
                  ? 'Atualizar Pagamento' 
                  : 'Gerar Pagamento'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
